/**
 * WHAT: Gallery Page JavaScript
 * WHY: Handle dynamic backgrounds, image loading, and gallery interactions
 * HOW: Using modern JavaScript with API integration
 * IMPORTANCE: Core gallery functionality
 */

import apiService from './api.js';
import CONFIG from './config/config.js';
import DOMPurify from 'dompurify';

class GalleryManager {
    constructor() {
        this.images = [];
        this.currentFilter = 'all';
        this.currentImageIndex = 0;
        this.batchSize = 12;
        this.visibleCount = 12;
        this.filteredImages = [];
        
        this.init();
    }

    async init() {
        try {
            /* Full-page background matches menu: body.gallery::before + scripts/script.js → background-rotator.js */
            await this.loadGalleryImages();
            this.setupEventListeners();
            this.updateStats();
        } catch (error) {
            console.error('Gallery initialization error:', error);
            this.showError('Failed to load gallery content');
        }
    }

    async loadGalleryImages() {
        try {
            const response = await fetch(apiService.getApiUrl('/gallery'));
            const data = await response.json();
            
            if (data.success) {
                // The API returns data.data as the gallery object, which has an images array
                this.images = data.data.images || [];
                this.visibleCount = this.batchSize;
                this.renderGallery();
            } else {
                this.showError('Failed to load gallery images');
            }
        } catch (error) {
            console.error('Failed to load gallery images:', error);
            this.showError('Failed to load gallery images');
        }
    }

    renderGallery() {
        const grid = document.getElementById('gallery-grid');
        if (!grid) return;

        // Clear loading state
        grid.innerHTML = '';

        if (this.images.length === 0) {
            grid.innerHTML = '';
            const emptyDiv = document.createElement('div');
            emptyDiv.className = 'gallery__empty';
            const emptyP = document.createElement('p');
            emptyP.textContent = 'No images available yet. Check back soon!';
            emptyDiv.appendChild(emptyP);
            grid.appendChild(emptyDiv);
            return;
        }

        // Filter images based on current filter
        const filteredImages = this.filterImages(this.images, this.currentFilter);
        this.filteredImages = filteredImages;
        this.modalImages = filteredImages;
        const visibleImages = filteredImages.slice(0, this.visibleCount);

        // Render images
        visibleImages.forEach((image, index) => {
            const imageElement = this.createImageElement(image, index);
            grid.appendChild(imageElement);
        });

        this.updateLoadMoreButton();
    }

    filterImages(images, filter) {
        // Ensure images is an array
        if (!Array.isArray(images)) {
            return [];
        }
        
        switch (filter) {
            case 'featured':
                {
                    const featured = images.filter(img => {
                        const isFlagged = Boolean(img && (img.isFeatured === true));
                        const byCategory = typeof img?.category === 'string' && img.category.toLowerCase() === 'featured';
                        const byTags = Array.isArray(img?.tags) && img.tags.map(t => String(t).toLowerCase()).includes('featured');
                        return isFlagged || byCategory || byTags;
                    });
                    // If nothing is explicitly featured, fall back to all images to avoid an empty view
                    return featured.length > 0 ? featured : images;
                }
            case 'recent':
                return [...images]
                    .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))
                    .slice(0, 10);
            case 'popular':
                return [...images]
                    .sort((a, b) => (Number(b.viewCount) || 0) - (Number(a.viewCount) || 0))
                    .slice(0, 10);
            default:
                return images;
        }
    }

    createImageElement(image, index) {
        const element = document.createElement('div');
        element.className = 'gallery-item';
        element.dataset.index = index;
        
        // Normalize image URL (supports imageUrl, url, path, filename)
        let normalizedUrl = image.imageUrl || image.url || image.path || image.filename || '';
        if (normalizedUrl && !(normalizedUrl.startsWith('http://') || normalizedUrl.startsWith('https://'))) {
            if (normalizedUrl.startsWith('/')) {
                normalizedUrl = CONFIG.api.baseUrl + normalizedUrl;
            } else {
                normalizedUrl = CONFIG.getAssetUrl(normalizedUrl);
            }
        }

        // WHAT: Sanitize all user-controlled data before inserting into DOM
        // WHY: Prevent XSS attacks from malicious gallery image data
        // HOW: Use DOMPurify to sanitize each field individually
        const sanitizedTitle = DOMPurify.sanitize(image.title || 'Untitled');
        const sanitizedDescription = DOMPurify.sanitize(image.description || '');
        const sanitizedCategory = DOMPurify.sanitize(image.category || 'General');
        // WHAT: Sanitize image URL - DOMPurify handles URLs safely
        // WHY: Prevent malicious URLs or script injection via image src
        const sanitizedImageUrl = DOMPurify.sanitize(normalizedUrl);
        const sanitizedImageAlt = sanitizedTitle || 'Gallery image';
        const formattedDate = this.formatDate(image.createdAt);
        const sanitizedDate = DOMPurify.sanitize(formattedDate);
        
        // WHAT: Sanitize the entire HTML template string
        // WHY: Even though individual values are sanitized, sanitize the whole HTML for defense-in-depth
        // HOW: Use DOMPurify to sanitize the complete HTML string
        const galleryItemHtml = `
            <img src="${sanitizedImageUrl}" 
                 alt="${sanitizedImageAlt}" 
                 class="gallery-item__image"
                 loading="lazy">
            <div class="gallery-item__info">
                <h3 class="gallery-item__title">${sanitizedTitle}</h3>
                <p class="gallery-item__description">${sanitizedDescription}</p>
                <div class="gallery-item__meta">
                    <span class="gallery-item__category">${sanitizedCategory}</span>
                    <span class="gallery-item__date">${sanitizedDate}</span>
                </div>
            </div>
        `;
        element.innerHTML = DOMPurify.sanitize(galleryItemHtml);

        // Add click event for modal
        element.addEventListener('click', () => {
            this.openImageModal(index);
        });

        return element;
    }

    openImageModal(index) {
        const modal = document.getElementById('image-modal');
        const modalImage = document.getElementById('modal-image');
        const modalTitle = document.getElementById('modal-title');
        const modalDescription = document.getElementById('modal-description');
        const modalCategory = document.getElementById('modal-category');
        const modalDate = document.getElementById('modal-date');

        if (!modal || !modalImage) return;

        const image = this.modalImages?.[index];
        if (!image) return;
        this.currentImageIndex = index;

        modalImage.src = image.imageUrl;
        modalImage.alt = image.title || 'Gallery image';
        modalTitle.textContent = image.title || 'Untitled';
        modalDescription.textContent = image.description || '';
        modalCategory.textContent = image.category || 'General';
        modalDate.textContent = this.formatDate(image.createdAt);

        modal.style.display = 'block';
        document.body.style.overflow = 'hidden';
    }

    closeImageModal() {
        const modal = document.getElementById('image-modal');
        if (modal) {
            modal.style.display = 'none';
            document.body.style.overflow = 'auto';
        }
    }

    navigateImage(direction) {
        const total = this.modalImages?.length || 0;
        if (total === 0) return;

        const newIndex = direction === 'next' 
            ? (this.currentImageIndex + 1) % total
            : (this.currentImageIndex - 1 + total) % total;
        
        this.openImageModal(newIndex);
    }

    updateStats() {
        const totalImages = document.getElementById('total-images');
        const featuredImages = document.getElementById('featured-images');
        const categoriesCount = document.getElementById('categories-count');

        // Ensure this.images is an array
        const images = Array.isArray(this.images) ? this.images : [];

        if (totalImages) {
            totalImages.textContent = images.length;
        }

        if (featuredImages) {
            const featured = images.filter(img => img.isFeatured).length;
            featuredImages.textContent = featured;
        }

        if (categoriesCount) {
            const categories = new Set(images.map(img => img.category).filter(Boolean));
            categoriesCount.textContent = categories.size;
        }
    }

    formatDate(dateString) {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    }

    showError(message) {
        const grid = document.getElementById('gallery-grid');
        if (grid) {
            // WHAT: Sanitize error message and entire HTML template
            // WHY: Prevent XSS attacks from malicious error messages
            // HOW: Use DOMPurify to sanitize both the message and the complete HTML string
            const sanitizedMessage = DOMPurify.sanitize(message || 'An error occurred');
            const errorHtml = `
                <div class="gallery__error">
                    <p>${sanitizedMessage}</p>
                    <button onclick="location.reload()" class="btn btn-primary">Retry</button>
                </div>
            `;
            grid.innerHTML = DOMPurify.sanitize(errorHtml);
        }
    }

    setupEventListeners() {
        // Filter buttons
        const filterButtons = document.querySelectorAll('.filter-btn');
        filterButtons.forEach(button => {
            button.addEventListener('click', (e) => {
                const filter = e.currentTarget.dataset.filter;
                if (filter) this.setFilter(filter);
            });
        });

        const loadMoreButton = document.getElementById('galleryLoadMore');
        if (loadMoreButton) {
            loadMoreButton.addEventListener('click', () => {
                this.visibleCount += this.batchSize;
                this.renderGallery();
            });
        }

        // Modal close
        const closeButtons = document.querySelectorAll('.close');
        closeButtons.forEach(button => {
            button.addEventListener('click', () => {
                this.closeImageModal();
            });
        });

        // Modal navigation
        const prevButton = document.getElementById('prev-image');
        const nextButton = document.getElementById('next-image');
        
        if (prevButton) {
            prevButton.addEventListener('click', () => {
                this.navigateImage('prev');
            });
        }
        
        if (nextButton) {
            nextButton.addEventListener('click', () => {
                this.navigateImage('next');
            });
        }

        // Close modal on outside click
        const modal = document.getElementById('image-modal');
        if (modal) {
            modal.addEventListener('click', (e) => {
                if (e.target === modal) {
                    this.closeImageModal();
                }
            });
        }

        // Keyboard navigation
        document.addEventListener('keydown', (e) => {
            if (document.getElementById('image-modal').style.display === 'block') {
                switch (e.key) {
                    case 'Escape':
                        this.closeImageModal();
                        break;
                    case 'ArrowLeft':
                        this.navigateImage('prev');
                        break;
                    case 'ArrowRight':
                        this.navigateImage('next');
                        break;
                }
            }
        });
    }

    setFilter(filter) {
        this.currentFilter = filter;
        this.visibleCount = this.batchSize;
        
        // Update active button
        document.querySelectorAll('.filter-btn').forEach(btn => {
            btn.classList.remove('active');
        });
        document.querySelector(`[data-filter="${filter}"]`).classList.add('active');
        
        this.renderGallery();
    }

    updateLoadMoreButton() {
        const loadMoreButton = document.getElementById('galleryLoadMore');
        if (!loadMoreButton) return;

        const hasMore = this.filteredImages.length > this.visibleCount;
        loadMoreButton.style.display = hasMore ? 'inline-flex' : 'none';
    }

    destroy() {
        /* no-op: page background timers live in background-rotator.js */
    }
}

// Initialize gallery when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
    const gallery = new GalleryManager();
    
    // Cleanup on page unload
    window.addEventListener('beforeunload', () => {
        gallery.destroy();
    });
}); 